// src/components/Navbar.jsx
// Main global navigation bar for Saar 3
// Handles:
// - Logo navigation
// - Google-style search dropdown
// - Auth-based buttons (Profile / Add Topic / Notifications / Logout)
// - Login / Signup for logged-out users

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import {
  searchTopics,
  getTopicByWord,
  primeTopicCache,
} from "../api/topicAPI";
import { lockAppScroll, unlockAppScroll } from "../utils/scrollLock";
import { buildProfilePath, buildTopicPath } from "../utils/publicPaths";
import NotificationDropdown from "./NotificationDropdown";
import {
  warmAddTopicTransition,
  warmOwnProfileTransition,
  warmTopicTransition,
} from "../utils/routeTransitionWarmup";
import SearchResultsPanel from "./SearchResultsPanel";

import "./Navbar.css";

function Navbar() {
  const navRef = useRef(null);

  // Set CSS var for navbar height (for app-main padding)
  useEffect(() => {
    function setNavHeight() {
      if (navRef.current) {
        const h = navRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty("--nav-h", `${h}px`);
      }
    }

    setNavHeight();
    window.addEventListener("resize", setNavHeight);
    return () => window.removeEventListener("resize", setNavHeight);
  }, []);

  // Auth state
   const { user, logout } = useContext(AuthContext);
  // Mobile "More" menu state
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  // English: Desktop profile menu used only on the user's own profile page.
const [showDesktopProfileMenu, setShowDesktopProfileMenu] = useState(false);
const desktopProfileMenuRef = useRef(null);
  // Routing helpers
  const location = useLocation();
  const navigate = useNavigate();
    // Centralized logout redirect so every navbar logout path behaves the same.
  const handleLogoutRedirect = useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);
  // English: Detect when the logged-in user is currently on their own profile page.
  const isOwnProfilePage =
  !!user && location.pathname === buildProfilePath(user);

  // Search input value
  const [navSearch, setNavSearch] = useState("");

  // Search dropdown data
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Dropdown state
  const [showDropdown, setShowDropdown] = useState(false);
  const typingTimer = useRef(null);
  const dropdownRef = useRef(null);
  const requestSeq = useRef(0);

  // Lock scroll while dropdown open
  useEffect(() => {
    if (showDropdown) lockAppScroll();
    else unlockAppScroll();

    return () => unlockAppScroll();
  }, [showDropdown]);

  // Close "More" menu on outside click (mobile)
  useEffect(() => {
    function handleMoreOutside(e) {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    }

    if (showMoreMenu) document.addEventListener("mousedown", handleMoreOutside);
    return () =>
      document.removeEventListener("mousedown", handleMoreOutside);
  }, [showMoreMenu]);

  // English: Close desktop profile menu when clicked outside.
useEffect(() => {
  function handleDesktopMenuOutside(e) {
    if (
      desktopProfileMenuRef.current &&
      !desktopProfileMenuRef.current.contains(e.target)
    ) {
      setShowDesktopProfileMenu(false);
    }
  }

  if (showDesktopProfileMenu) {
    document.addEventListener("mousedown", handleDesktopMenuOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleDesktopMenuOutside);
  };
}, [showDesktopProfileMenu]);

  // Detect Home page (Home already has big search bar)
  const isHome = location.pathname === "/";

  useEffect(() => {
  // English: Close both mobile and desktop action menus on route change.
  setShowMoreMenu(false);
  setShowDesktopProfileMenu(false);
}, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);


  // Run search API

  const runSearch = async (text) => {
    const query = text.trim();
    const seq = ++requestSeq.current;

    if (!query) {
      setResults([]);
      setTotal(0);
      setHasExactMatch(false);
      setError("");
      setShowDropdown(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      // Public search is cookie-safe; no token param is allowed/needed.
      const res = await searchTopics({
        query,
        page: 1,
        limit: 10,
      });

      if (seq !== requestSeq.current) return; // ignore stale response

      const topics = res?.topics || [];
      setResults(topics);
      setTotal(res?.total || topics.length);

      // Check exact word match
      const exact = topics.some(
        (t) => t.word?.toLowerCase() === query.toLowerCase()
      );
      setHasExactMatch(exact);

      setShowDropdown(true);
    } catch (err) {
      if (seq !== requestSeq.current) return;

      console.error("Navbar search failed:", err);
      setResults([]);
      setTotal(0);
      setHasExactMatch(false);
      setError("Failed to search topics. Please try again.");
      setShowDropdown(true);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  // Handle typing (with debounce)

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setNavSearch(value);

    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
    }

    typingTimer.current = setTimeout(() => {
      runSearch(value);
    }, 400);
  };

  // Search submit (Enter / Search)

 const handleSubmit = async (e) => {
  e.preventDefault();

  const query = navSearch.trim();
  if (!query) return;

  if (!query.includes(" ")) {
    const exactLocal = results.find(
      (t) => (t.word || "").trim().toLowerCase() === query.toLowerCase()
    );

    if (exactLocal) {
      handleResultClick(exactLocal);
      return;
    }

    try {
      const data = await getTopicByWord(query);

      if (data?.exists && data?.topic) {
        handleResultClick(data.topic);
        return;
      }
    } catch (err) {
      console.error("Navbar exact lookup failed:", err);
    }

    await runSearch(query);
    return;
  }

  runSearch(query);
};

  // Click on topic result

 const handleResultClick = (topic) => {
  primeTopicCache(topic);
  warmTopicTransition(topic);
  navigate(buildTopicPath(topic), { state: { topic } });
  setShowDropdown(false);
};

  // Reset search when going Home
  useEffect(() => {
    if (isHome) {
      setNavSearch("");
      setResults([]);
      setTotal(0);
      setHasExactMatch(false);
      setError("");
      setShowDropdown(false);
    }
  }, [isHome]);

  return (
    <nav className="navbar" ref={navRef}>
      {/* LEFT: Logo */}
      <div className="navbar-left">
        <Link to="/" className="logo-link">
          <h2 className="logo">Saar 3</h2>
        </Link>
      </div>

      {/* CENTER: Search bar (hidden on Home page) */}
      <div
        className={`nav-search ${isHome ? "hide-search" : ""}`}
        ref={dropdownRef}
      >
        <form onSubmit={handleSubmit} className="nav-search-form">
          <input
            type="text"
            className="nav-search-input"
            placeholder="Search any word..."
            value={navSearch}
            onChange={handleSearchChange}
            onFocus={() => navSearch && setShowDropdown(true)}
          />
          <button type="submit" className="nav-search-btn">
            Search
          </button>
        </form>

        {/* Dropdown results */}
        {!isHome && showDropdown && (
          <div className="navbar-search-dropdown">
            <SearchResultsPanel
              query={navSearch}
              loading={loading}
              results={results}
              total={total}
              hasExactMatch={hasExactMatch}
              error={error}
              onAddExactTopic={() =>
                navigate("/add-topic", {
                  state: { prefillWord: navSearch.trim() },
                })
              }
              onResultClick={handleResultClick}
              onSuggestionClick={async (word) => {
  const trimmed = (word || "").trim();
  if (!trimmed) return;

  const localMatch = results.find(
    (t) => (t.word || "").trim().toLowerCase() === trimmed.toLowerCase()
  );

  if (localMatch) {
    handleResultClick(localMatch);
    return;
  }

  try {
    const data = await getTopicByWord(trimmed);
    if (data?.exists && data?.topic) {
      handleResultClick(data.topic);
      return;
    }
  } catch (err) {
    console.error("Navbar suggestion lookup failed:", err);
  }

  navigate(buildTopicPath(trimmed));
}}
            />
          </div>
        )}
      </div>

      {/* RIGHT: Buttons */}
      <div className="navbar-right">
        {user ? (
          <>
            {/* Top-right corner group (Desktop) */}
            <div className="nav-corner-actions">
<Link
  to="/add-topic"
  className="nav-btn add-topic-btn-nav"
  onMouseEnter={warmAddTopicTransition}
  onFocus={warmAddTopicTransition}
  onTouchStart={warmAddTopicTransition}
>
  Add Topic
  <span className="add-topic-plus">+</span>
</Link>

<Link
  to={buildProfilePath(user)}
  className="nav-btn"
  onMouseEnter={() => warmOwnProfileTransition(user)}
  onFocus={() => warmOwnProfileTransition(user)}
  onTouchStart={() => warmOwnProfileTransition(user)}
>
  Profile
</Link>

{/* English: Desktop bell stays visible in the top-right area. */}
<span className="notif-desktop">
  <NotificationDropdown />
</span>

{/* English: On the user's own profile page, move account actions into a 3-dot menu. */}
{isOwnProfilePage ? (
  <div className="nav-desktop-more-wrap" ref={desktopProfileMenuRef}>
    <button
      type="button"
      className="nav-desktop-more"
      aria-label="More"
      title="More"
      onClick={() => setShowDesktopProfileMenu((v) => !v)}
    >
      ⋮
    </button>

    {showDesktopProfileMenu && (
      <div className="nav-desktop-dropdown">
        <button
          type="button"
          className="nav-desktop-item"
          onClick={() => {
            setShowDesktopProfileMenu(false);
            navigate(buildProfilePath(user), { state: { openEditProfile: true } });
          }}
        >
          Edit profile
        </button>

        <button
          type="button"
          className="nav-desktop-item danger"
          onClick={() => {
  setShowDesktopProfileMenu(false);
  navigate("/delete-account");
}}
        >
          Delete account
        </button>

        <button
          type="button"
          className="nav-desktop-item"
          onClick={async () => {
            setShowDesktopProfileMenu(false);
            await handleLogoutRedirect();
          }}
        >
          Logout
        </button>
      </div>
    )}
  </div>
) : (
    <button
    onClick={handleLogoutRedirect}
    className="nav-btn nav-logout-btn"
  >
    Logout
  </button>
)}
            </div>

            {/* Mobile actions: Bell + Profile + 3 dots */}
            <div className="nav-mobile-actions" ref={moreMenuRef}>
              <span className="nav-mobile-bell">
                <NotificationDropdown />
              </span>

       <Link
  to={buildProfilePath(user)}
  className="nav-mobile-profile"
  aria-label="Profile"
  onMouseEnter={() => warmOwnProfileTransition(user)}
  onFocus={() => warmOwnProfileTransition(user)}
  onTouchStart={() => warmOwnProfileTransition(user)}
>
  👤
</Link>

              <button
  type="button"
  className="nav-mobile-more"
  aria-label="More"
  onClick={() => setShowMoreMenu((v) => !v)}
>
  ⋮
</button>

              
{showMoreMenu && (
  <div className="nav-more-dropdown">
  <Link
  to="/add-topic"
  className="nav-more-item"
  onMouseEnter={warmAddTopicTransition}
  onFocus={warmAddTopicTransition}
  onTouchStart={warmAddTopicTransition}
  onClick={() => setShowMoreMenu(false)}
>
  Add Topic
</Link>
    
        <button
      type="button"
      className="nav-more-item"
      onClick={() => {
        setShowMoreMenu(false);
        navigate(buildProfilePath(user), { state: { openEditProfile: true } });
      }}
    >
      Edit profile
    </button>
    
       <button
      type="button"
      className="nav-more-item"
      onClick={async () => {
        setShowMoreMenu(false);
        await handleLogoutRedirect();
      }}
    >
      Logout
    </button>

    <button
      type="button"
      className="nav-more-item danger"
      onClick={() => {
  setShowMoreMenu(false);
  navigate("/delete-account");
}}
    >
      Delete account
    </button>
  </div>
)}
              
            </div>
          </>
               ) : (
          <>
            <Link
              to="/login"
              className="nav-btn"
              state={{
                returnTo: `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`,
              }}
            >
              Login
            </Link>

            <Link
              to="/signup"
              className="nav-btn"
              state={{
                returnTo: `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`,
              }}
            >
              Signup
            </Link>
          </>
        )}
      </div>
    </nav>
  );
}

export default Navbar;



